import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_BASE = `${BACKEND_URL}/api/admin`;

const adminClient = axios.create({
  baseURL: ADMIN_BASE,
  timeout: 20000,
  withCredentials: true,  // send httpOnly tgm_admin_jwt cookie on every request
});

adminClient.interceptors.request.use((config) => {
  // Cookie-based auth is primary (httpOnly tgm_admin_jwt) — sent automatically by browser.
  // The master X-Admin-Token header remains as a fallback for CLI/curl emergency access.
  const masterToken = localStorage.getItem('tgmart_admin_token');
  if (masterToken) {
    config.headers['X-Admin-Token'] = masterToken;
  }
  return config;
});

export const adminApi = {
  list: (resource) => adminClient.get(`/${resource}`).then((r) => r.data),
  // Server-side paginated vendors list — required because the suppliers
  // collection has 20 000+ rows and the generic /suppliers GET only returns
  // 2 000 max. Returns { items, total, limit, skip }.
  listSuppliersPaginated: ({ limit = 50, skip = 0, q = '', status = '' } = {}) => {
    const params = { limit, skip };
    if (q) params.q = q;
    if (status) params.status = status;
    return adminClient.get('/suppliers/paginated', { params }).then((r) => r.data);
  },
  create: (resource, payload) => adminClient.post(`/${resource}`, payload).then((r) => r.data),
  update: (resource, id, payload) => adminClient.put(`/${resource}/${id}`, payload).then((r) => r.data),
  remove: (resource, id) => adminClient.delete(`/${resource}/${id}`).then((r) => r.data),
  enquiries: () => adminClient.get('/enquiries').then((r) => r.data),
  requirements: () => adminClient.get('/requirements').then((r) => r.data),
  // verify token by trying a lightweight call
  ping: () => adminClient.get('/main_categories').then((r) => r.data),

  // Phase 4 — analytics
  analyticsSummary: () => adminClient.get('/analytics/summary').then((r) => r.data),
  analyticsTimeline: (days = 30) => adminClient.get('/analytics/timeline', { params: { days } }).then((r) => r.data),
  analyticsTopCategories: (limit = 10) => adminClient.get('/analytics/top-categories', { params: { limit } }).then((r) => r.data),
  analyticsTopVendors: (limit = 10) => adminClient.get('/analytics/top-vendors', { params: { limit } }).then((r) => r.data),

  // SEO — XML sitemaps
  getSitemaps: () => adminClient.get('/sitemaps').then((r) => r.data),
  saveSitemaps: (payload) => adminClient.put('/sitemaps', payload).then((r) => r.data),
  pingSitemaps: () => adminClient.post('/sitemaps/ping', {}).then((r) => r.data),

  // Phase 4 — CSV import
  importVendorsCsv: (file, dryRun = false) => {
    const fd = new FormData(); fd.append('file', file); fd.append('dry_run', dryRun ? 'true' : 'false');
    return adminClient.post('/import/vendors', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }).then((r) => r.data);
  },
  importProductsCsv: (file, dryRun = false) => {
    const fd = new FormData(); fd.append('file', file); fd.append('dry_run', dryRun ? 'true' : 'false');
    return adminClient.post('/import/products', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }).then((r) => r.data);
  },
  importCitiesCsv: (file, dryRun = false) => {
    const fd = new FormData(); fd.append('file', file); fd.append('dry_run', dryRun ? 'true' : 'false');
    return adminClient.post('/import/cities', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }).then((r) => r.data);
  },

  // Phase 4 — lead rules + audit
  listLeadRules: () => adminClient.get('/lead-rules').then((r) => r.data),
  createLeadRule: (payload) => adminClient.post('/lead-rules', payload).then((r) => r.data),
  updateLeadRule: (id, payload) => adminClient.put(`/lead-rules/${id}`, payload).then((r) => r.data),
  removeLeadRule: (id) => adminClient.delete(`/lead-rules/${id}`).then((r) => r.data),
  listAuditLogs: (limit = 100) => adminClient.get('/audit-logs', { params: { limit } }).then((r) => r.data),

  // Image upload
  uploadImage: (file, prefix = 'product') => {
    const fd = new FormData(); fd.append('file', file); fd.append('prefix', prefix);
    return adminClient.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },

  // Admin impersonation
  impersonateVendor: (vendorId) => adminClient.post(`/impersonate/vendor/${vendorId}`).then((r) => r.data),
  impersonateCustomer: (userId) => adminClient.post(`/impersonate/customer/${userId}`).then((r) => r.data),

  // Welcome emails
  sendWelcomeVendor: (vendorId) => adminClient.post(`/vendors/${vendorId}/send-welcome`).then((r) => r.data),
  sendWelcomeCustomer: (userId) => adminClient.post(`/customers/${userId}/send-welcome`).then((r) => r.data),
  sendWelcomeBulk: (resource, ids) => adminClient.post(`/${resource}/send-welcome-bulk`, { ids }).then((r) => r.data),
  bulkDelete: (resource, ids) => adminClient.post(`/${resource}/bulk-delete`, { ids }).then((r) => r.data),
  giftCredits: (supplierId, credits, note) => adminClient.post(`/suppliers/${supplierId}/gift-credits`, { credits, note }).then((r) => r.data),
  bulkCustomerStatus: (ids, status) => adminClient.post('/customers/bulk-status', { ids, status }).then((r) => r.data),

  // Site Settings
  getSettings: (key) => adminClient.get(`/settings/${key}`).then((r) => r.data),
  saveSettings: (key, payload) => adminClient.put(`/settings/${key}`, payload).then((r) => r.data),
  runInboxDigest: () => adminClient.post('/inbox/run-digest', {}).then((r) => r.data),
  testEmail: (to) => adminClient.post('/settings/email/test', { to }).then((r) => r.data),
  testMsg91: (to) => adminClient.post('/settings/msg91/test', { to }).then((r) => r.data),
  testStorage: () => adminClient.post('/settings/storage/test', {}).then((r) => r.data),
  testSmsLogin: (to) => adminClient.post('/settings/smslogin/test', { to }).then((r) => r.data),

  // CMS pages
  listPages: () => adminClient.get('/pages').then((r) => r.data),
  createPage: (payload) => adminClient.post('/pages', payload).then((r) => r.data),
  updatePage: (slug, payload) => adminClient.put(`/pages/${slug}`, payload).then((r) => r.data),
  deletePage: (slug) => adminClient.delete(`/pages/${slug}`).then((r) => r.data),

  // AI (Gemini)
  aiGetDefaults: () => adminClient.get('/settings/ai/defaults').then((r) => r.data),
  aiTest: (prompt) => adminClient.post('/settings/ai/test', { prompt }).then((r) => r.data),
  aiCmsGenerate: (title, instructions) =>
    adminClient.post('/ai/cms-generate', { title, instructions: instructions || '' }).then((r) => r.data),
  aiCmsRewrite: (content, tone, instructions) =>
    adminClient.post('/ai/cms-rewrite', { content, tone: tone || 'professional', instructions: instructions || '' }).then((r) => r.data),
  aiAnalyzeRequirement: (reqId) =>
    adminClient.post(`/ai/requirement/${reqId}/analyze`, {}).then((r) => r.data),

  // Certificates
  listCertificates: (params = {}) => adminClient.get('/certificates', { params }).then((r) => r.data),
  verifyCertificate: (id, payload) => adminClient.put(`/certificates/${id}`, payload).then((r) => r.data),
  deleteCertificate: (id) => adminClient.delete(`/certificates/${id}`).then((r) => r.data),

  // Admin user management (JWT-based)
  authLogin: (email, password) =>
    adminClient.post('/auth/login', { email, password }).then((r) => r.data),
  authVerifyMfa: (challenge_token, otp) =>
    adminClient.post('/auth/verify-mfa', { challenge_token, otp }).then((r) => r.data),
  authMe: () => adminClient.get('/auth/me').then((r) => r.data),
  authChangePassword: (current_password, new_password) =>
    adminClient.post('/auth/change-password', { current_password, new_password }).then((r) => r.data),

  // Admin Users CRUD
  listAdminUsers: () => adminClient.get('/admin-users').then((r) => r.data),
  createAdminUser: (payload) => adminClient.post('/admin-users', payload).then((r) => r.data),
  updateAdminUser: (id, payload) => adminClient.put(`/admin-users/${id}`, payload).then((r) => r.data),
  deleteAdminUser: (id) => adminClient.delete(`/admin-users/${id}`).then((r) => r.data),
  resetAdminUserPassword: (id) =>
    adminClient.post(`/admin-users/${id}/reset-password`).then((r) => r.data),

  // Payments (Razorpay)
  listPayments: (params = {}) => adminClient.get('/payments', { params }).then((r) => r.data),
  refundPayment: (orderId, payload = {}) =>
    adminClient.post(`/payments/${orderId}/refund`, payload).then((r) => r.data),
  listPaymentEvents: (params = {}) =>
    adminClient.get('/payments-events', { params }).then((r) => r.data),
  listSubscriptions: () => adminClient.get('/subscriptions').then((r) => r.data),

  // Logout — clears the httpOnly cookie server-side
  logout: () => adminClient.post('/auth/logout').then((r) => r.data),

  // Backups
  listBackupCollections: () => adminClient.get('/backups/collections').then((r) => r.data),
  listBackups: (limit = 50) => adminClient.get('/backups', { params: { limit } }).then((r) => r.data),
  createBackup: (collections) =>
    adminClient.post('/backups', { collections: collections || ['*'] }).then((r) => r.data),
  deleteBackup: (id) => adminClient.delete(`/backups/${id}`).then((r) => r.data),
  getBackupSchedule: () => adminClient.get('/backups/schedule').then((r) => r.data),
  saveBackupSchedule: (payload) =>
    adminClient.put('/backups/schedule', payload).then((r) => r.data),
  restoreBackup: (file, mode = 'replace', collections = '') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('mode', mode);
    if (collections) fd.append('collections', collections);
    return adminClient.post('/backups/restore', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    }).then((r) => r.data);
  },
  backupDownloadUrl: (id) => `${ADMIN_BASE}/backups/${id}/download`,
};

// --- Auth-state helpers ---
// JWT now lives in an httpOnly cookie (tgm_admin_jwt) — JS cannot read it.
// We expose the cached user profile via localStorage `tgmart_admin_me` so the UI
// has something to render before /auth/me resolves.
export const getAdminMe = () => {
  try { return JSON.parse(localStorage.getItem('tgmart_admin_me') || 'null'); } catch { return null; }
};
export const setAdminMe = (u) => localStorage.setItem('tgmart_admin_me', JSON.stringify(u || null));
export const clearAdminMe = () => localStorage.removeItem('tgmart_admin_me');

// Legacy localStorage-JWT helpers are kept as no-ops for backwards-compat with any
// caller that still references them; the actual JWT lives in the httpOnly cookie now.
export const getAdminJwt = () => null;
export const setAdminJwt = () => { /* no-op — cookie is authoritative */ };
export const clearAdminJwt = () => { /* no-op — server clears via /auth/logout */ };

export const getAdminToken = () => localStorage.getItem('tgmart_admin_token');
export const setAdminToken = (t) => localStorage.setItem('tgmart_admin_token', t);
export const clearAdminToken = () => localStorage.removeItem('tgmart_admin_token');

// Returns true if EITHER a master token is in localStorage OR a cached admin profile exists.
// This is a "best-effort" client check — definitive auth state must come from /auth/me.
export const hasAdminAuth = () =>
  !!(localStorage.getItem('tgmart_admin_token') || localStorage.getItem('tgmart_admin_me'));
